/**
 * Cart Preview Modal Functionality
 * Handles showing/hiding cart preview and cart operations
 */

class CartPreview {
  constructor() {
    this.modal = null;
    this.closeBtn = null;
    this.init();
    
    // Listen for cart updates from other parts of the site
    this.listenForCartUpdates();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupModal());
    } else {
      this.setupModal();
    }
  }
  
  setupModal() {
    this.modal = document.getElementById('cart-preview-modal');
    this.closeBtn = this.modal?.querySelector('.cart-preview-close');
    
    if (this.modal) {
      this.bindEvents();
      this.bindToggleButtons();
    }
  }
  
  listenForCartUpdates() {
    // Listen for custom cart update events
    document.addEventListener('cart:updated', () => {
      this.refreshModalContent();
    });
    
    // Listen for Shopify's cart update events
    document.addEventListener('cart.requestComplete', () => {
      this.refreshModalContent();
    });
    
    // Also listen for any AJAX cart updates
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.target.id === 'CartCount' || 
             mutation.target.classList.contains('shopping-bag-count'))) {
          this.refreshModalContent();
        }
      });
    });
    
    // Observe cart count elements for changes
    const cartCountElements = document.querySelectorAll('#CartCount, .shopping-bag-count');
    cartCountElements.forEach(element => {
      observer.observe(element, { attributes: true });
    });
  }

  bindEvents() {
    // Close modal when clicking overlay
    const overlay = this.modal?.querySelector('.cart-preview-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.hide());
    }
    
    // Close modal when clicking close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.hide());
    }
    
    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hide();
      }
    });
    
    // Bind remove item buttons
    this.bindRemoveButtons();
  }

  bindToggleButtons() {
    const toggleButtons = document.querySelectorAll('.js-cart-preview-toggle');
    toggleButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.show(e);
      });
    });
  }

  async show(event) {
    if (!this.modal) return;
    
    // Always refresh modal content when opening
    await this.refreshModalContent();
    
    // Check if we're on desktop (cart icon click) or mobile
    const isDesktop = window.innerWidth >= 769;
    
    if (isDesktop) {
      // On desktop, position relative to the clicked cart icon
      const clickedButton = event?.target?.closest('.js-cart-preview-toggle') || 
                           document.querySelector('.js-cart-preview-toggle');
      
      if (clickedButton) {
        const buttonRect = clickedButton.getBoundingClientRect();
        const modalContent = this.modal.querySelector('.cart-preview-content');
        const overlay = this.modal.querySelector('.cart-preview-overlay');
        
        // Hide overlay on desktop
        if (overlay) {
          overlay.style.display = 'none';
        }
        
        // Position modal below the cart icon
        // modalContent.style.top = (buttonRect.bottom + 10) + 'px';
        // modalContent.style.left = 'auto';
        // modalContent.style.right = '20px';
        // modalContent.style.transform = 'none';
        // modalContent.style.zIndex = '10000';
      }
    } else {
      // On mobile, show overlay and reset positioning
      const overlay = this.modal.querySelector('.cart-preview-overlay');
      if (overlay) {
        overlay.style.display = 'block';
      }
      
      const modalContent = this.modal.querySelector('.cart-preview-content');
      if (modalContent) {
        modalContent.style.position = ''; // Reset to CSS defaults
        modalContent.style.top = '';
        modalContent.style.left = '';
        modalContent.style.right = '';
        modalContent.style.transform = '';
        modalContent.style.zIndex = '';
      }
    }
    
    this.modal.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Add class to body for mobile padding control
    document.body.classList.add('cart-preview-active');
    
    // Focus close button for accessibility
    this.closeBtn?.focus();
  }

  hide() {
    if (!this.modal) return;
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
    // Remove class from body to remove mobile padding
    document.body.classList.remove('cart-preview-active');
  }

  updateCartCount(count) {
    const title = this.modal.querySelector('.cart-preview-header h3');
    if (title) {
      title.textContent = `My Bag (${count})`;
    }
  }

  async removeItem(event) {
    const btn = event.currentTarget;
    const itemKey = btn.dataset.itemKey;
    
    if (!itemKey) return;
    
    try {
      // Show loading state
      btn.disabled = true;
      btn.innerHTML = '<span>...</span>';
      
      // Remove item from cart
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      const cart = await response.json();
      
      // Update cart count in header
      this.updateHeaderCartCount(cart.item_count);
      
      // Update modal content with new cart data
      this.updateModalWithCartData(cart);
      
      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: cart },
        bubbles: true 
      }));
      
    } catch (error) {
      console.error('Error removing item:', error);
      // Reset button state
      btn.disabled = false;
      btn.innerHTML = '<span>&times;</span>';
    }
  }

  updateHeaderCartCount(count) {
    // Update cart count in header
    const headerCartCount = document.getElementById('CartCount');
    if (headerCartCount) {
      const countSpan = headerCartCount.querySelector('span');
      if (countSpan) {
        countSpan.textContent = count;
      }
      
      // Hide count if cart is empty
      if (count === 0) {
        headerCartCount.style.display = 'none';
      } else {
        headerCartCount.style.display = 'flex';
      }
    }

    // Update universal nav cart count
    const universalCartCount = document.querySelector('.shopping-bag-count');
    if (universalCartCount) {
      universalCartCount.textContent = count;
      
      // Hide count if cart is empty
      if (count === 0) {
        universalCartCount.style.display = 'none';
      } else {
        universalCartCount.style.display = 'block';
      }
    }
  }

  async refreshModalContent() {
    if (!this.modal) return;
    
    try {
      console.log('Refreshing modal content...');
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cartData = await response.json();
        console.log('Cart data received:', cartData);
        this.updateModalWithCartData(cartData);
        this.updateCartCount(cartData.item_count);
        this.updateHeaderCartCount(cartData.item_count);
      }
    } catch (error) {
      console.error('Error refreshing modal content:', error);
    }
  }
  
  // Public method to manually refresh cart
  refreshCart() {
    this.refreshModalContent();
  }

  async updateModalWithCartData(cartData) {
    if (!this.modal) return;
    
    console.log('Updating modal with cart data:', cartData);
    
    // Update the title
    const title = this.modal.querySelector('.cart-preview-header h3');
    if (title) {
      title.textContent = `My Bag (${cartData.item_count})`;
    }
    
    // Update the items section
    const itemsContainer = this.modal.querySelector('.cart-preview-items');
    if (itemsContainer && cartData.items && cartData.items.length > 0) {
      let itemsHTML = '';
      
      // Generate items HTML
      for (const item of cartData.items) {
        console.log('Item data:', item);
        
        // Check if this is a gap paper bag (exclude from being clickable)
        const isPaperBag = this.isPaperBag(item);
        
        if (isPaperBag) {
          // Paper bag - not clickable
          itemsHTML += `
            <div class="cart-preview-item">
              <div class="cart-preview-item-image">
                <img src="${item.image}" 
                     alt="${item.title}"
                     width="60" height="60"
                     loading="lazy">
              </div>
              <div class="cart-preview-item-details">
                <div class="cart-preview-item-title">${item.product_title}</div>
                <div class="cart-preview-item-variant">${item.variant_title}</div>
                <div class="cart-preview-item-price">${this.formatMoney(item.final_price)}</div>
              </div>
              <button type="button" class="cart-preview-item-remove" 
                      data-item-key="${item.key}" 
                      aria-label="Remove ${item.product_title}">
                <span>&times;</span>
              </button>
            </div>
          `;
        } else {
          // Regular product - clickable
          // Try different ways to get the product URL
          const productUrl = await this.getProductUrl(item);
          console.log('Product URL for', item.product_title, ':', productUrl);
          
          itemsHTML += `
            <div class="cart-preview-item">
              <a href="${productUrl}" class="cart-preview-item-link">
                <div class="cart-preview-item-image">
                  <img src="${item.image}" 
                       alt="${item.title}"
                       width="60" height="60"
                       loading="lazy">
                </div>
                <div class="cart-preview-item-details">
                  <div class="cart-preview-item-title">${item.product_title}</div>
                  <div class="cart-preview-item-variant">${item.variant_title}</div>
                  <div class="cart-preview-item-price">${this.formatMoney(item.final_price)}</div>
                </div>
              </a>
              <button type="button" class="cart-preview-item-remove" 
                      data-item-key="${item.key}" 
                      aria-label="Remove ${item.product_title}">
                <span>&times;</span>
              </button>
            </div>
          `;
        }
      }
      
      itemsContainer.innerHTML = itemsHTML;
      
      // Rebind remove buttons
      this.bindRemoveButtons();
    } else if (itemsContainer) {
      itemsContainer.innerHTML = '<div class="cart-preview-empty"><p>Your bag is empty</p></div>';
    }
    
    // Update subtotal
    const subtotalContainer = this.modal.querySelector('.cart-preview-subtotal');
    console.log('Subtotal container found:', subtotalContainer);
    if (subtotalContainer) {
      if (cartData.items && cartData.items.length > 0) {
        console.log('Setting subtotal for items:', cartData.items.length);
        subtotalContainer.innerHTML = `
          <span>Subtotal:</span>
          <span>${this.formatMoney(cartData.total_price)}</span>
        `;
        subtotalContainer.style.display = 'block';
        console.log('Subtotal display set to block');
      } else {
        console.log('Setting subtotal for empty cart');
        subtotalContainer.innerHTML = `
          <span>Subtotal:</span>
          <span>P0.00</span>
        `;
        subtotalContainer.style.display = 'block';
        console.log('Subtotal display set to block');
      }
    } else {
      console.log('Subtotal container NOT found!');
    }
    
    // Show/hide actions based on cart items
    const actionsContainer = this.modal.querySelector('.cart-preview-actions');
    console.log('Actions container found:', actionsContainer);
    if (actionsContainer) {
      if (cartData.items && cartData.items.length > 0) {
        console.log('Setting actions display to block for items:', cartData.items.length);
        actionsContainer.style.display = 'block';
      } else {
        console.log('Setting actions display to block for empty cart');
        actionsContainer.style.display = 'block';
      }
    } else {
      console.log('Actions container NOT found!');
    }
  }
  
  formatMoney(cents) {
    // Use Shopify's money format if available, otherwise fallback to simple formatting
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    } else {
      // Fallback formatting
      const amount = (cents / 100).toFixed(2);
      return `$${amount}`;
    }
  }
  
  bindRemoveButtons() {
    const removeBtns = this.modal.querySelectorAll('.cart-preview-item-remove');
    removeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.removeItem(e));
    });
  }

  // Helper method to identify gap paper bags
  isPaperBag(item) {
    // Check by product title, handle, or variant ID
    const productTitle = item.product_title?.toLowerCase() || '';
    const productHandle = item.product_handle?.toLowerCase() || '';
    const variantTitle = item.variant_title?.toLowerCase() || '';
    
    // Common paper bag identifiers
    const paperBagKeywords = [
      'paper bag',
      'paperbag',
      'paper-bag',
      'gap paper bag',
      'gap paperbag'
    ];
    
    // Check if any keyword matches
    return paperBagKeywords.some(keyword => 
      productTitle.includes(keyword) || 
      productHandle.includes(keyword) || 
      variantTitle.includes(keyword)
    );
  }

  // Helper method to get product URL
  async getProductUrl(item) {
    console.log('Getting product URL for item:', item);
    
    // Method 1: Try product_handle (most reliable)
    if (item.product_handle) {
      return `/products/${item.product_handle}`;
    }
    
    // Method 2: Try to extract handle from the URL if it exists
    if (item.url) {
      // Extract product handle from URL like /products/product-handle
      const urlMatch = item.url.match(/\/products\/([^\/\?]+)/);
      if (urlMatch && urlMatch[1]) {
        return `/products/${urlMatch[1]}`;
      }
    }
    
    // Method 3: Try product_id (fallback)
    if (item.product_id) {
      // We'll need to fetch the product handle using the ID
      return this.getProductHandleById(item.product_id);
    }
    
    // Method 4: Try to construct from product title (last resort)
    if (item.product_title) {
      const handle = this.titleToHandle(item.product_title);
      return `/products/${handle}`;
    }
    
    console.warn('Could not determine product URL for item:', item);
    return '#'; // Fallback to prevent broken links
  }
  
  // Helper method to convert product title to handle
  titleToHandle(title) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim();
  }
  
  // Helper method to get product handle by ID (async)
  async getProductHandleById(productId) {
    try {
      const response = await fetch(`/products.json`);
      if (response.ok) {
        const products = await response.json();
        const product = products.products.find(p => p.id === productId);
        if (product) {
          return `/products/${product.handle}`;
        }
      }
    } catch (error) {
      console.error('Error fetching product handle:', error);
    }
    return '#'; // Fallback
  }
}

// Initialize cart preview when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if modal exists in DOM
  const modalElement = document.getElementById('cart-preview-modal');
  
  if (modalElement) {
    window.cartPreview = new CartPreview();
  } else {
    console.error('Modal element still not found after timeout');
    
    // Let's search for any elements with similar IDs
    const allElements = document.querySelectorAll('[id*="cart"]');
    console.log('Elements with "cart" in ID:', allElements);
    
    // Try again with a longer timeout
    setTimeout(() => {
      const modalElementSecondTimeout = document.getElementById('cart-preview-modal');
      
      if (modalElementSecondTimeout) {
        window.cartPreview = new CartPreview();
      }
    }, 500);
  }
});

// Export for use in other scripts
window.CartPreview = CartPreview;

// Global function to refresh cart preview from other scripts
window.refreshCartPreview = function() {
  if (window.cartPreview) {
    window.cartPreview.refreshCart();
  }
};

// Listen for any cart changes globally
document.addEventListener('DOMContentLoaded', function() {
  // Create a custom event that can be triggered when cart changes
  window.triggerCartUpdate = function() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
  };
  
  // Also listen for common cart update patterns
  const cartUpdateSelectors = [
    '[data-cart-update]',
    '.cart-update',
    '.add-to-cart',
    '.cart-add'
  ];
  
  cartUpdateSelectors.forEach(selector => {
    document.addEventListener('click', function(e) {
      if (e.target.closest(selector)) {
        // Wait a bit for the cart to update, then refresh
        setTimeout(() => {
          window.refreshCartPreview();
        }, 500);
      }
    });
  });
});
